import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Download, Eye, FileText, Mail, Printer, Search, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  EmptyRows,
  PortalActionButton,
  PortalBadge,
  PortalPageHeader,
  PortalPanel,
  PortalStatCard,
  RefreshButton,
  SummaryLine,
} from "@/components/admin/PortalPrimitives";
import { cn, formatCurrency } from "@/lib/utils";

function txLabel(tx: any) {
  const category = String(tx.category || tx.type || "").toLowerCase();
  if (category.includes("payment")) return "Loan Repayment";
  if (category.includes("loan")) return "Loan";
  if (category.includes("penalty")) return "Adjustment";
  if (category.includes("contribution") || category.includes("saving")) return "Savings Deposit";
  return "Transaction";
}

function txAmounts(tx: any) {
  const category = String(tx.category || tx.type || "").toLowerCase();
  const amount = Number(tx.amount || 0);
  if (category.includes("loan") && !category.includes("payment")) {
    return { debit: amount, credit: 0 };
  }
  if (category.includes("penalty")) {
    return { debit: amount, credit: 0 };
  }
  return { debit: 0, credit: amount };
}

export default function Statements() {
  const [search, setSearch] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState<string | number | null>(null);

  const membersQuery = useQuery(
    ["statement-members", search],
    async () => {
      const params = new URLSearchParams({ page: "1", limit: "10" });
      if (search.trim()) params.set("search", search.trim());
      const response = await api.get(`/members?${params.toString()}`);
      return response.data?.results || response.data?.data || [];
    },
    { staleTime: 15000 }
  );

  const members = Array.isArray(membersQuery.data) ? membersQuery.data : [];

  useEffect(() => {
    if (!selectedMemberId && members.length > 0) {
      setSelectedMemberId(members[0].id);
    }
  }, [members, selectedMemberId]);

  const statementQuery = useQuery(
    ["statement-detail", selectedMemberId],
    async () => {
      const response = await api.get(`/reports/member/${selectedMemberId}/statement`);
      return response.data?.data;
    },
    { enabled: !!selectedMemberId, staleTime: 15000 }
  );

  const statement = statementQuery.data || {};
  const member = statement.member || members.find((item: any) => item.id === selectedMemberId);
  const transactions = statement.transactions || [];
  const summary = statement.summary || {};
  const selectedName = member?.fullNames || member?.fullName || `${member?.firstName || ""} ${member?.lastName || ""}`.trim() || "Select a member";

  const rows = useMemo(() => {
    let balance = 0;
    const chronologicalRows = [...transactions]
      .sort((a: any, b: any) => new Date(a.date || a.createdAt).getTime() - new Date(b.date || b.createdAt).getTime())
      .map((tx: any) => {
        const amounts = txAmounts(tx);
        balance += Number(amounts.credit || 0) - Number(amounts.debit || 0);
        return { ...tx, ...amounts, balance };
      });
    return chronologicalRows.reverse();
  }, [transactions, summary]);

  const loading = membersQuery.isLoading || statementQuery.isLoading;

  return (
    <div className="space-y-5 py-4">
      <PortalPageHeader
        title="Statement"
        description="View complete account statements and transaction history."
        actions={
          <>
            <PortalActionButton onClick={() => selectedMemberId && statementQuery.refetch()}>
              <FileText className="mr-2 h-4 w-4" /> Generate Statement
            </PortalActionButton>
            <PortalActionButton variant="outline" onClick={() => window.print()}>
              <Printer className="mr-2 h-4 w-4" /> Print
            </PortalActionButton>
          </>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[1fr_290px]">
        <div className="space-y-5">
          <Card className="rounded-lg border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="grid gap-3 lg:grid-cols-[1fr_180px_180px_180px_auto]">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Search Member</label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setSelectedMemberId(null);
                      }}
                      className="h-10 pl-9"
                      placeholder="Search by member name or ID..."
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Account</label>
                  <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                    <option>All Accounts</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Transaction Type</label>
                  <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                    <option>All Types</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Member</label>
                  <select
                    value={selectedMemberId || ""}
                    onChange={(event) => setSelectedMemberId(event.target.value)}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  >
                    {members.map((item: any) => (
                      <option key={item.id} value={item.id}>
                        {item.fullNames || item.fullName || `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.memberCode || item.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" className="h-10">
                    <Download className="mr-2 h-4 w-4" /> Export
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-col gap-4 md:flex-row md:items-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#009A44]/10 text-[#009A44]">
                  <UserRound className="h-8 w-8" />
                </div>
                <div className="min-w-0 flex-1">
                  {loading ? <Skeleton className="h-7 w-48" /> : <h2 className="font-bold text-slate-950">{selectedName}</h2>}
                  <p className="mt-1 text-sm text-slate-500">
                    {member?.memberCode || member?.memberNumber || selectedMemberId || "-"} · {member?.phone || member?.email || "No contact saved"}
                  </p>
                </div>
                <PortalBadge tone={String(member?.membershipStatus || member?.status || "").toLowerCase().includes("inactive") ? "red" : "green"}>
                  {member?.membershipStatus || member?.status || "Active"}
                </PortalBadge>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-4">
            <PortalStatCard title="Total Savings" value={formatCurrency(Number(summary.totalSavings || 0))} icon={<FileText className="h-5 w-5" />} tone="green" loading={loading} />
            <PortalStatCard title="Total Loans" value={formatCurrency(Number(summary.totalLoans || 0))} icon={<FileText className="h-5 w-5" />} tone="blue" loading={loading} />
            <PortalStatCard title="Total Repaid" value={formatCurrency(Number(summary.totalRepaid || 0))} icon={<FileText className="h-5 w-5" />} tone="purple" loading={loading} />
            <PortalStatCard title="Outstanding" value={formatCurrency(Number(summary.outstandingLoans || summary.outstandingBalance || 0))} icon={<FileText className="h-5 w-5" />} tone="red" loading={loading} />
          </div>

          <PortalPanel title="All Transactions" action={<Button variant="outline" size="sm"><Download className="mr-2 h-4 w-4" /> Download Excel</Button>}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500">
                    <th className="px-3 py-3 font-semibold">Date & Time</th>
                    <th className="px-3 py-3 font-semibold">Transaction ID</th>
                    <th className="px-3 py-3 font-semibold">Type</th>
                    <th className="px-3 py-3 font-semibold">Description</th>
                    <th className="px-3 py-3 text-right font-semibold">Debit (FRW)</th>
                    <th className="px-3 py-3 text-right font-semibold">Credit (FRW)</th>
                    <th className="px-3 py-3 text-right font-semibold">Balance (FRW)</th>
                    <th className="px-3 py-3 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, index) => (
                      <tr key={index}><td className="px-3 py-4" colSpan={8}><Skeleton className="h-5 w-full" /></td></tr>
                    ))
                  ) : rows.length === 0 ? (
                    <EmptyRows colSpan={8} message="No transactions found for the selected member." />
                  ) : (
                    rows.map((tx: any) => (
                      <tr key={`${tx.category}-${tx.id}`} className="hover:bg-slate-50/70">
                        <td className="whitespace-nowrap px-3 py-3 text-slate-600">{tx.date ? new Date(tx.date).toLocaleString() : "-"}</td>
                        <td className="px-3 py-3 font-mono text-xs text-slate-600">TXN-{String(tx.id).padStart(6, "0")}</td>
                        <td className="px-3 py-3"><PortalBadge tone={tx.debit > 0 ? "amber" : "green"}>{txLabel(tx)}</PortalBadge></td>
                        <td className="max-w-[280px] truncate px-3 py-3 font-medium text-slate-900">{tx.description || tx.purpose || tx.reason || txLabel(tx)}</td>
                        <td className={cn("px-3 py-3 text-right font-semibold", tx.debit > 0 ? "text-red-600" : "text-slate-400")}>{tx.debit > 0 ? tx.debit.toLocaleString() : "-"}</td>
                        <td className={cn("px-3 py-3 text-right font-semibold", tx.credit > 0 ? "text-[#009A44]" : "text-slate-400")}>{tx.credit > 0 ? tx.credit.toLocaleString() : "-"}</td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-950">{Number(tx.balance || 0).toLocaleString()}</td>
                        <td className="px-3 py-3 text-center"><Button variant="ghost" size="icon" className="h-8 w-8"><Eye className="h-4 w-4" /></Button></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </PortalPanel>
        </div>

        <div className="space-y-5">
          <PortalPanel title="Member Information">
            <div className="space-y-1">
              <SummaryLine label="Member ID" value={member?.memberCode || selectedMemberId || "-"} />
              <SummaryLine label="Phone" value={member?.phone || "-"} />
              <SummaryLine label="Email" value={member?.email || "-"} />
              <SummaryLine label="Status" value={member?.membershipStatus || member?.status || "Active"} tone="green" />
            </div>
          </PortalPanel>
          <PortalPanel title="Statement Summary">
            <div className="divide-y divide-slate-100">
              <SummaryLine label="Opening Balance" value={0} money />
              <SummaryLine label="Total Credit" value={Number(summary.totalSavings || 0) + Number(summary.totalRepaid || 0)} tone="green" money />
              <SummaryLine label="Total Debit" value={Number(summary.totalLoans || 0) + Number(summary.penalties || 0)} tone="red" money />
              <SummaryLine label="Closing Balance" value={Number(summary.totalSavings || 0) - Number(summary.outstandingLoans || summary.outstandingBalance || 0)} tone="green" money />
            </div>
          </PortalPanel>
          <PortalPanel title="Quick Actions">
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start"><Mail className="mr-2 h-4 w-4 text-[#009A44]" /> Send Statement to Member</Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => window.print()}><Printer className="mr-2 h-4 w-4 text-[#009A44]" /> Print Statement</Button>
              <Button variant="outline" className="w-full justify-start"><FileText className="mr-2 h-4 w-4 text-[#009A44]" /> Account Snapshot</Button>
            </div>
          </PortalPanel>
        </div>
      </div>
    </div>
  );
}
